package dev.lingvo.site.pages.app.settings.billing

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.layout.Layout
import dev.lingvo.shared.PlanStatus
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.ds.LAsyncContent
import dev.lingvo.site.ds.LMuted
import dev.lingvo.site.ds.PageHeader
import dev.lingvo.site.components.billing.PortalButton
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.ds.LCard
import dev.lingvo.site.ds.LSectionTitle
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.TypeScale
import dev.lingvo.site.state.WorkspaceActionAuthority
import dev.lingvo.site.state.WorkspaceActionClaim
import dev.lingvo.site.state.WorkspacePrivilege
import dev.lingvo.site.state.WorkspaceRequestContext
import dev.lingvo.site.pages.app.settings.loadWorkspaceProtectedResource
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Ul
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.trf
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.i18n.tf

/**
 * A payment did not go through.
 *
 * The page says what has **not** been taken away, first and plainly. A dunning message that only
 * threatens makes somebody assume the worst has already happened, stop trusting every other number
 * on the screen, and open a support ticket about work that is sitting exactly where they left it.
 *
 * An admin gets the explanation **and the owner's name**, because the useful thing for them is
 * knowing who to go to — not a payment form they cannot complete for somebody else's card.
 */
@Page("/app/settings/billing/fix-payment")
@Layout(".components.layouts.AppLayout")
@Composable
fun FixPaymentPage() {
    val workspaceContext = WorkspaceRequestContext.current()
    val workspace = workspaceContext?.workspace
    val authority = WorkspaceActionAuthority.resolve(workspaceContext)

    Column(Modifier.fillMaxWidth().gap(Space.XL)) {
        PageHeader(t("app.settings.billing.fix_payment.payment", "Payment"), t("app.settings.billing.fix_payment.what_happened_and_what_has_not", "What happened, and what has not."))

        if (workspaceContext == null || !authority.canReadBilling) {
            LAlert(
                title = t("app.settings.billing.fix_payment.payment_details_are_managed_by_an", "Payment details are managed by an owner or admin"),
                description = t("app.settings.billing.fix_payment.ask_a_workspace_owner_or_admin", "Ask a workspace owner or admin to review the subscription status."),
                variant = LAlertVariant.INFO,
            )
            return@Column
        }

        val state = loadWorkspaceProtectedResource(
            context = workspaceContext,
            privilege = WorkspacePrivilege.BILLING_READ,
            transport = ApiClient::workspaceBilling,
        )

        LAsyncContent(
            state,
            rows = 2,
            loadingLabel = t("app.settings.billing.fix_payment.loading_billing", "Loading billing"),
            // A failed status read cannot establish payment or non-payment.
            failure = {
                LAlert(
                    t("app.settings.billing.fix_payment.we_cant_read_your_billing_right", "We can't read your billing right now"),
                    t("app.settings.billing.fix_payment.payment_status_unavailable", "Payment status could not be confirmed. Check billing again before making another payment."),
                    variant = LAlertVariant.WARNING,
                )
            },
        ) { billing ->
            when {
                billing == null -> LAlert(
                    t("app.settings.billing.fix_payment.billing_status_unknown", "Billing status is unavailable"),
                    t("app.settings.billing.fix_payment.payment_status_unavailable", "Payment status could not be confirmed. Check billing again before making another payment."),
                    variant = LAlertVariant.INFO,
                )

                billing.status != PlanStatus.PAST_DUE -> LAlert(
                    t("app.settings.billing.fix_payment.no_overdue_payment_reported", "No overdue payment reported"),
                    t("app.settings.billing.fix_payment.current_status_not_payment_evidence", "This workspace's current billing status does not report an overdue payment. Check billing for the current plan and recorded documents."),
                    variant = LAlertVariant.INFO,
                )

                else -> LCard(Modifier.fillMaxWidth()) {
                    Column(Modifier.fillMaxWidth().gap(Space.MD)) {
                        // A heading, not bold text. `PageHeader` emits this page's only `H1`, so
                        // the sentence the whole card explains was unreachable from a screen
                        // reader's heading list.
                        LSectionTitle(paymentFailureHeadline(workspace?.name))
                        // The reassurance before the instruction. This is the part people need.
                        Ul {
                            Fact(t("app.settings.billing.fix_payment.nothing_has_been_switched_off_translating", "Nothing has been switched off. Translating, importing, exporting and the API all work exactly as before."))
                            Fact(t("app.settings.billing.fix_payment.nothing_has_been_deleted_and_nothing", "Nothing has been deleted, and nothing will be."))
                            Fact(t("app.settings.billing.fix_payment.we_retry_over_the_next_few", "We retry over the next few weeks. If every attempt fails the subscription ends and the workspace moves to Free."))
                            Fact(t("app.settings.billing.fix_payment.usually_this_is_an_expired_card", "Usually this is an expired card rather than anything to worry about."))
                        }

                        if (authority.canUseOwnerBilling) {
                            LMuted(
                                t("app.settings.billing.fix_payment.the_processor_handles_the_payment_details", "The processor handles the payment details — Lingvo never sees them."),
                            )
                            PortalButton(
                                WorkspaceActionClaim.capture(
                                    workspaceContext,
                                    WorkspacePrivilege.OWNER_BILLING,
                                ),
                                t("app.settings.billing.fix_payment.update_payment_method", "Update payment method"),
                            )
                        } else {
                            // The useful fact for an admin is a name, not a form.
                            LMuted(
                                t("app.settings.billing.fix_payment.only_the_owner_can_update_the_2", "Only the owner can update the card, because it is theirs. " +
                                    "Ask them to open Billing — they will have had the email too."),
                            )
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun Fact(text: String) {
    Li { Span(Modifier.fontSize(TypeScale.BodySmall).toAttrs()) { Text(text) } }
}

/**
 * The sentence the whole recovery card explains, and this page's only `h2`.
 *
 * A raw Kotlin template under a translated page header, with the workspace name spliced at a
 * position no locale could move — on the page that decides whether the product keeps being paid
 * for. The three facts under it were catalogued and the heading over them was not.
 */
internal fun paymentFailureHeadline(workspaceName: String?): String = trf(
    "app.settings.billing.fix_payment.a_payment_for_workspace_did_not",
    "A payment for {workspace} did not go through.",
    "workspace" to (
        workspaceName
            ?: tr("app.settings.billing.fix_payment.this_workspace", "this workspace")
        ),
)
